/**
 * Применение операций к сцене на стороне клиента.
 *
 * ЭТО ВТОРАЯ РЕАЛИЗАЦИЯ ОДНОЙ СЕМАНТИКИ. Первая — `hub/internal/ops`
 * на сервере, и обычно такое дублирование — прямая дорога к тихому
 * расхождению. Здесь оно неизбежно: клиент обязан применять дельты
 * сам, иначе каждое движение слоя требовало бы полного снапшота,
 * и весь смысл дельт пропал бы.
 *
 * Раз избежать нельзя — расхождение надо СДЕЛАТЬ ЗАМЕТНЫМ. Для этого
 * есть общая фикстура: сервер записывает пары «сцена + операции →
 * результат», а этот код обязан выдавать ровно то же. Файлы фикстур
 * порождает Go, потому что ядро операций там авторитетно; TypeScript
 * их только читает. Разошлись — упал тест, а не эфир.
 *
 * Отличие от сервера одно и намеренное: здесь НЕ строятся обратные
 * операции. Undo живёт на сервере (общий стек на канал, docs/05),
 * и клиенту достаточно уметь применять.
 */
/** Что пошло не так при применении. */
export class ApplyError extends Error {
    reason;
    constructor(reason, message) {
        super(message);
        this.reason = reason;
        this.name = 'ApplyError';
    }
}
/**
 * Применяет пачку операций, возвращая НОВУЮ сцену.
 *
 * Исходная не меняется: у клиента на неё смотрит отрисовка, и правка
 * на месте означала бы, что сравнить «было» и «стало» уже нечем.
 */
export function applyOps(scene, ops) {
    let next = scene;
    for (const op of ops) {
        next = applyOne(next, op);
    }
    return next;
}
/**
 * Применяет одну операцию.
 *
 * Сортировка вынесена сюда, а не разложена по веткам, — ровно как
 * на сервере, где она стоит одна на всё применение. Разложить
 * по веткам значило бы однажды забыть её в одной из них: фикстура
 * поймала именно это на правке сцены, где слои не трогаются вовсе,
 * а порядок всё равно обязан быть каноническим.
 */
function applyOne(scene, op) {
    return sorted(applyKind(scene, op));
}
function applyKind(scene, op) {
    switch (op.kind.case) {
        case 'layerAdd':
            return addLayer(scene, op.kind.value.layer);
        case 'layerRemove':
            return removeLayer(scene, op.kind.value.layerId);
        case 'layerUpdate': {
            const { layerId, patch } = op.kind.value;
            return updateLayer(scene, layerId, (layer) => patchLayer(layer, patch));
        }
        case 'layerReorder': {
            const { layerId, z } = op.kind.value;
            return updateLayer(scene, layerId, (layer) => ({ ...layer, z }));
        }
        case 'sceneUpdate':
            return patchScene(scene, op.kind.value.patch);
        case 'sceneApply': {
            const incoming = op.kind.value.scene;
            if (incoming === undefined) {
                throw new ApplyError('no-scene', 'пресет не содержит сцены');
            }
            // Личность сцены не меняется: идентификатор и канал остаются
            // прежними. Пресет приносит содержимое, а не подменяет сцену
            // целиком — иначе ссылки на неё разом протухли бы
            return {
                ...scene,
                name: incoming.name,
                canvasW: incoming.canvasW,
                canvasH: incoming.canvasH,
                workArea: incoming.workArea,
                layers: incoming.layers,
            };
        }
        default:
            // Операция, которой этот клиент не знает. Отдельная ошибка,
            // а не «пропустить»: пропуск означал бы, что клиент тихо
            // разошёлся с сервером и продолжил рисовать не то
            throw new ApplyError('unknown-op', `неизвестная операция: ${String(op.kind.case)}`);
    }
}
function addLayer(scene, layer) {
    if (layer === undefined) {
        throw new ApplyError('no-scene', 'операция добавления без слоя');
    }
    if (scene.layers.some((l) => l.id === layer.id)) {
        throw new ApplyError('layer-exists', `слой уже есть: ${layer.id}`);
    }
    return { ...scene, layers: [...scene.layers, layer] };
}
function removeLayer(scene, layerId) {
    const layers = scene.layers.filter((l) => l.id !== layerId);
    if (layers.length === scene.layers.length) {
        throw new ApplyError('layer-not-found', `слоя нет: ${layerId}`);
    }
    return { ...scene, layers };
}
function updateLayer(scene, layerId, change) {
    const index = scene.layers.findIndex((l) => l.id === layerId);
    if (index < 0) {
        throw new ApplyError('layer-not-found', `слоя нет: ${layerId}`);
    }
    const layers = [...scene.layers];
    const current = layers[index];
    if (current === undefined) {
        throw new ApplyError('layer-not-found', `слоя нет: ${layerId}`);
    }
    layers[index] = change(current);
    return { ...scene, layers };
}
/**
 * Применяет патч слоя.
 *
 * Незаданное поле НЕ трогается — это не то же самое, что «задано
 * пустым». Два модератора, правящие разные свойства одного слоя,
 * не должны затирать работу друг друга.
 */
function patchLayer(layer, patch) {
    if (patch === undefined) {
        return layer;
    }
    const next = { ...layer };
    if (patch.name !== undefined)
        next.name = patch.name;
    if (patch.transform !== undefined)
        next.transform = patch.transform;
    if (patch.hidden !== undefined)
        next.hidden = patch.hidden;
    if (patch.locked !== undefined)
        next.locked = patch.locked;
    if (patch.props !== undefined) {
        next.props = mergeProps(layer.props, patch.props);
    }
    return next;
}
/**
 * Сливает свойства по ключам ВЕРХНЕГО уровня.
 *
 * Значение null удаляет ключ — так устроен JSON Merge Patch
 * (RFC 7386), и сервер поступает ровно так же. Придумать здесь своё
 * соглашение значило бы получить расхождение, которое проявится
 * при первом же undo.
 */
function mergeProps(current, patch) {
    const merged = { ...current };
    for (const [key, value] of Object.entries(patch)) {
        if (value === null) {
            delete merged[key];
            continue;
        }
        merged[key] = value;
    }
    return merged;
}
function patchScene(scene, patch) {
    if (patch === undefined) {
        throw new ApplyError('no-scene', 'правка сцены без патча');
    }
    const next = { ...scene };
    if (patch.name !== undefined)
        next.name = patch.name;
    if (patch.canvasW !== undefined)
        next.canvasW = patch.canvasW;
    if (patch.canvasH !== undefined)
        next.canvasH = patch.canvasH;
    if (patch.workArea !== undefined)
        next.workArea = patch.workArea;
    return next;
}
/**
 * Приводит слои к каноническому порядку: по z, при равных z — по id.
 *
 * Тот же порядок, что на сервере, и по той же причине: порядок
 * в массиве не должен нести смысла сам по себе. Иначе после
 * переподключения слои с равным z встали бы иначе, и картинка
 * у зрителя изменилась бы без единой операции.
 */
function sorted(scene) {
    const layers = [...scene.layers].sort((a, b) => (a.z !== b.z ? a.z - b.z : compare(a.id, b.id)));
    return { ...scene, layers };
}
function compare(a, b) {
    if (a < b)
        return -1;
    if (a > b)
        return 1;
    return 0;
}
//# sourceMappingURL=apply.js.map